import React, { useContext, useEffect, useState } from 'react';
import { AiOutlineFileExcel, AiOutlineFundView, AiOutlinePrinter } from 'react-icons/ai';
import { Link, useNavigate } from 'react-router-dom';
import { LoginContext } from '../../../reactcontext/ReactContext';
import axios from 'axios';
import swal from 'sweetalert';
import { CSVLink } from 'react-csv';
import logo from '../../../assets/logo.avif';

const ExpiredPassports = () => {
  const apiUrl = process.env.REACT_APP_API_URL
  const { loginInfo } = useContext(LoginContext);
  const navigate = useNavigate();
  // No preview toggle, always show report
  const [records, setRecords] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [flattenedData, setFlattenedData] = useState([])

  useEffect(() => {
    !loginInfo.login && navigate('/');
  }, [loginInfo]);

  function getReadableTimestamp() {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const readableTimestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    return readableTimestamp;
  }

  const generateReport = (elementId) => {
    // Don't print if data is still loading or empty
    if (loading || flattenedData.length === 0) {
      alert('No data available to print. Please wait for data to load.');
      return;
    }

    const element = document.getElementById(elementId);
    if (element) {
      // Remove padding classes that cause top gap
      element.classList.remove('p-5', 'px-5');
      
      // Create print-specific styles
      const printStyles = document.createElement('style');
      printStyles.innerHTML = `
        @media print {
          @page {
            margin: 0;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
          body * { visibility: hidden; }
          #${elementId}, #${elementId} * { visibility: visible; }
          #${elementId} {
            margin: 0 !important;
            padding: 0 !important;
            font-size: 10px;
            overflow: visible !important;
            page-break-inside: avoid;
          }
          table {
            page-break-inside: avoid;
            width: 100%;
            border-collapse: collapse;
            margin-top: 0 !important;
            height: auto !important;
          }
          tr {
            page-break-inside: avoid;
          }
          th, td {
            padding: 1px !important;
            text-align: left;
            font-size: 10px;
          }
          .header {
            margin-bottom: 0 !important;
            padding-top: 0 !important;
          }
        }
      `;
      document.head.appendChild(printStyles);

      // Print with a small delay to ensure styles are applied
      setTimeout(() => {
        window.print();
      }, 100);

      // Remove print styles and restore classes after printing
      setTimeout(() => {
        document.head.removeChild(printStyles);
        element.classList.add('p-5', 'px-5');
      }, 1000);
    }
  };

  useEffect(() => {
    axios
      .get(`${apiUrl}/employee/passportExpired`, {
        headers: {
          loginToken: localStorage.getItem('loginToken'),
        },
      })
      .then((res) => {
        if (res.data.error) {
          setErrorMessage(res.data.error);
        } else {
          setRecords(res.data.passports);
          console.log('Passport data:', res.data)
          setFlattenedData(prev => {
            return (res.data.passports || []).map((item) => {
              // Handle both flat and nested data structures
              const passport = item.passport || item;
              const employee = passport.Employee || {};
              const health = employee.Health || {};
              const agent = employee.Agent || {};
              
              return {
                passportNo: passport.passportNo || 'N/A',
                fullName: (employee.firstName && employee.lastName) ? `${employee.firstName} ${employee.lastName}` : 'N/A',
                nationality: employee.nationality || 'N/A',
                bloodGroup: health.bloodGroup || 'N/A',
                agent: agent.name || 'N/A',
                expiredOn: passport.expiryDate || 'N/A',
                days: item.daysUntilExpiry || 0,
              };
            });
          })
        }

        setLoading(false);
      })
      .catch((err) => {
        swal({
          title: 'Error',
          icon: 'error',
          text: JSON.stringify(err.message),
        });
        setLoading(false);
      });
  }, []);

  return (
    <div className='w-full flex h-full flex-col items-center justify-between relative gap-3'>
      <div className='self-start'>
        <Link to={'/dashboard'} className='text-yellow-700'>
          Dashboard
        </Link>
        {' > '}
        <Link to={'/reports'} className='text-yellow-700'>
          Reports
        </Link>
        {' > '}
        Expired Passports
      </div>

      <div className='w-full flex justify-center mt-5 gap-2 p-2'>
        <button
          className={`p-2 gap-1 flex items-center justify-center text-[13pt] rounded-md shadow-lg font-light ${
            loading || flattenedData.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-yellow-400 hover:bg-yellow-500'
          }`}
          onClick={() => generateReport('report-content')}
          disabled={loading || flattenedData.length === 0}
        >
          <AiOutlinePrinter fontSize={24} />
          Print
        </button>
        <CSVLink
          data={flattenedData}
          filename={`expired passports-` + getReadableTimestamp()}
          className={`p-2 gap-1 flex items-center justify-center text-[13pt] rounded-md shadow-lg font-light ${
            flattenedData.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-400 hover:bg-gray-500'
          }`}
        >
          <AiOutlineFileExcel fontSize={24} />
          Export CSV
        </CSVLink>
      </div>

      <div id='report-content' className='flex flex-col w-full p-5 px-5'>
        <img src={logo} alt='Logo' className='self-center w-20' />
        <h1 className='text-[18pt] font-semibold self-center'>Rahman Group</h1>
        <h1 className='text-[14pt] self-center mt-2'>Report Listing- Expired Passports</h1>
        <div className='flex flex-wrap justify-start w-full gap-10 mt-5'>
          <p className='italic'>
            Report Printed By : <span className='font-bold'>{loginInfo.userDetails.firstName + ' ' + loginInfo.userDetails.lastName}</span>
          </p>
          <p className='italic'>
            Printed On : <span className='font-bold'>{getReadableTimestamp()}</span>
          </p>
          <p className='italic'>
            Total Records : <span className='font-bold'>{records.length}</span>
          </p>
          <p className='italic'>
            Signature : <span className='font-bold'>______________</span>
          </p>
        </div>
        <div className='flex mt-5 flex-col gap-5'>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse table-auto">
              <thead>
                <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">Passport No</th>
                  <th className="py-3 px-6 text-left">Full Name</th>
                  <th className="py-3 px-6 text-left">Nationality</th>
                  <th className="py-3 px-6 text-left">Blood Group</th>
                  <th className="py-3 px-6 text-left">Agent</th>
                  <th className="py-3 px-6 text-left">Expired On</th>
                  <th className="py-3 px-6 text-left">Expired</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm font-light">
                {flattenedData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-6 px-6 text-center text-gray-400">No data available</td>
                  </tr>
                ) : (
                  flattenedData.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-100">
                      <td className="py-3 px-6 text-left whitespace-nowrap">{item.passportNo}</td>
                      <td className="py-3 px-6 text-left">{item.fullName}</td>
                      <td className="py-3 px-6 text-left">{item.nationality}</td>
                      <td className="py-3 px-6 text-left">{item.bloodGroup}</td>
                      <td className="py-3 px-6 text-left">{item.agent}</td>
                      <td className="py-3 px-6 text-left">{item.expiredOn}</td>
                      <td className="py-3 px-6 text-left">{Math.abs(parseInt(item.days)) + " days ago"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
     
    </div>
  );
};

export default ExpiredPassports;
